import { useEffect, useRef, useState } from "react";
import {
  emptyScene,
  type BoardScene,
} from "@/lib/canvas/board-schema";
import {
  emptyDrawing,
  type DrawingV2,
} from "@/lib/canvas/telestrator-schema";
import { useReviewEditorStore } from "@/stores/review-editor";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TacticalBoardEditor } from "./TacticalBoardEditor";
import { TelestratorCanvas } from "./TelestratorCanvas";

/**
 * Milestone-3 canvas demo route — exercises both editors so a
 * reviewer can poke at the M3 surfaces directly.
 *
 * Surface tab swaps which editor is active; per-surface state lives
 * in the shared review-editor store so a swap doesn't lose the
 * in-progress drag on the other surface. The scene and drawing are
 * both local component state — committed via the editors' onChange
 * callbacks. No server interaction yet (mutations land in M4).
 *
 * Throwaway scaffold — replaced by the real Coach Review shell in
 * Milestone 4.
 */

type Surface = "tactical" | "telestrator";

export function CanvasDemo() {
  const [surface, setSurface] = useState<Surface>("tactical");
  const [scene, setScene] = useState<BoardScene>(() => emptyScene());
  const [drawing, setDrawing] = useState<DrawingV2>(() => emptyDrawing());
  const [width, setWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Test-only debug bridge — exposes scene + drawing state so the
  // Playwright spec can introspect Konva node ids and validate the
  // canvas contract without needing a Konva-aware test framework.
  // Off in production: Vite tree-shakes the assignment via the
  // import.meta.env.MODE check below (also gated by a query string
  // so a regular user can't accidentally expose it).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const search = window.location.search;
    if (!search.includes("debug=1") && !import.meta.env.DEV) return;
    (window as unknown as {
      __tkticCanvas?: { scene: BoardScene; drawing: DrawingV2; surface: Surface };
    }).__tkticCanvas = { scene, drawing, surface };
  }, [scene, drawing, surface]);

  // Resize observer to keep the canvases responsive. Watches the
  // outer container so the canvas reflows when the window changes
  // size or the right-rail collapses on mobile.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        // Reserve some breathing room for the right-rail (220px min
        // + 16px gap on lg screens). On narrow screens the rail
        // wraps below so the canvas can use most of the width.
        const isWide = w >= 1024;
        const canvasW = Math.max(
          320,
          Math.floor(isWide ? w - 220 - 16 : w),
        );
        setWidth(canvasW);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const setStoreSurface = useReviewEditorStore((s) => s.setSurface);
  useEffect(() => {
    setStoreSurface(surface);
  }, [surface, setStoreSurface]);

  return (
    <main className="min-h-screen p-6 text-ink">
      <header className="mb-6 flex flex-col gap-2">
        <h1 className="text-3xl font-display tracking-tight">Canvas demo</h1>
        <p className="text-ink-muted text-sm max-w-2xl">
          Milestone 3 — both canvases full-parity with the legacy painter +
          tactical-board. Shared shape primitives (arrow / line / zone /
          label / freehand) work identically on both surfaces. Surface-
          specific tools (player / ball on the tactical board;
          spotlight / dim / formation on the telestrator) are exposed
          per-surface.
        </p>
      </header>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-display text-ink-muted uppercase tracking-wider">
          Surface
        </span>
        <div className="min-w-[12rem]">
          <TkticSelect
            value={surface}
            onChange={(v) => setSurface(v as Surface)}
            options={[
              { value: "tactical", label: "Tactical board" },
              { value: "telestrator", label: "Video telestrator" },
            ]}
            nativeSelectProps={{ "data-canvas-surface": "" }}
          />
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs font-mono text-ink-muted">
          <span data-testid="canvas-demo-counter">
            {surface === "tactical"
              ? `${scene.shapes.length} shape${scene.shapes.length === 1 ? "" : "s"} · ${scene.tokens.length} token${scene.tokens.length === 1 ? "" : "s"}`
              : `${drawing.objects.length} object${drawing.objects.length === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      <div ref={containerRef} className="w-full">
        {surface === "tactical" ? (
          <TacticalBoardEditor
            scene={scene}
            onSceneChange={setScene}
            width={width}
          />
        ) : (
          <TelestratorCanvas
            drawing={drawing}
            onDrawingChange={setDrawing}
            width={width}
            aspect={9 / 16}
          />
        )}
      </div>
    </main>
  );
}
